import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import DragDropListsCore from '@components-clean/core/drag-drop-lists-core';
import { DragDropListItemProps } from '@components-clean/core/drag-drop-lists-core/drag-drop-lists-core.interface';
import EditCard from '@components-clean/core/edit-card';
import ErrorMessage from '@components-clean/core/error';
import Loading from '@components-clean/core/loading/loading';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import { PanelModelType, PanelType } from 'types/graphql-global-types';
import { useBoolean, useTranslation } from '@hooks/index';
import { ShowError, ShowSuccess } from '@utils/notification';
import {
    ADD_STUDY_DASHBOARD_CONFIG_PANELS,
    GET_PANELS,
    GET_STUDY_DASHBOARD_CONFIG_PANELS,
} from '@queries/central-monitoring';
import {
    getPanels_OpraQueries_panels_Panel,
    getStudyDashboardConfigPanelsForStudy,
    getStudyDashboardConfigPanelsForStudyVariables,
    addStudyDashboardConfigPanels,
    addStudyDashboardConfigPanelsVariables,
} from '@queries/generated/central-monitoring.generated';

const COMPONENT_NAME = 'panelsConfig';

interface ConfigPanelsStateProps {
    display: (DragDropListItemProps & IPanel)[];
    available: (DragDropListItemProps & IPanel)[];
}

interface PanelsConfigProps {
    orgKey: string;
    studyRef: string;
    canEdit: boolean;
    panelData: (getPanels_OpraQueries_panels_Panel | null)[] | null;
}

interface IPanel {
    reference: string;
    title: string;
    active: boolean;
    [key: string]: unknown; // This keeps the types happy
}

const PanelsConfig = ({ orgKey, studyRef, canEdit, panelData }: PanelsConfigProps) => {
    const { confirmDiscardChanges, setComponentHasChanges } = useUnsavedChangesContext();

    const { t } = useTranslation();
    const isEditMode = useBoolean(false);

    const [saveConfig, { loading: isMutatingStudyPanelsConfigData, error: panelsMutationError }] = useMutation<
        addStudyDashboardConfigPanels,
        addStudyDashboardConfigPanelsVariables
    >(ADD_STUDY_DASHBOARD_CONFIG_PANELS, {
        refetchQueries: [
            {
                query: GET_PANELS,
                variables: { organisationId: orgKey, studyId: studyRef },
            },
            {
                query: GET_STUDY_DASHBOARD_CONFIG_PANELS,
                variables: { studyId: studyRef },
            },
        ],
    });

    const [getStudyDashboardConfigPanels, { data: getStudyDashboardConfigPanelsRawData }] = useLazyQuery<
        getStudyDashboardConfigPanelsForStudy,
        getStudyDashboardConfigPanelsForStudyVariables
    >(GET_STUDY_DASHBOARD_CONFIG_PANELS);

    const selectedPanelRefs = useMemo(() => {
        if (!getStudyDashboardConfigPanelsRawData?.getStudyDashboardConfigPanelsForStudy?.panelRefs) return [];

        return getStudyDashboardConfigPanelsRawData.getStudyDashboardConfigPanelsForStudy?.panelRefs
            .filter((panelRef) => panelRef)
            .map((panelRef) => panelRef!);
    }, [getStudyDashboardConfigPanelsRawData]);

    const allPanels: IPanel[] = useMemo(() => {
        if (!panelData) return [];

        const validPanelModelTypes = [PanelModelType.RATE_GROUP, PanelModelType.FUNNEL, PanelModelType.TRI_OPERATIONAL];

        return [
            {
                reference: 'SYS_SITESCORETREND',
                title: 'Site Score Trend',
                active: true,
            },
            ...panelData
                .filter(
                    (panel) =>
                        panel &&
                        panel.panelType &&
                        (panel.panelType === PanelType.TRI_OP_DATE ||
                            (panel.panelType === PanelType.MODEL &&
                                panel.modelParams?.type &&
                                validPanelModelTypes.includes(panel.modelParams.type))),
                )
                .map((panel) => {
                    return {
                        reference: panel!.id! as string,
                        active: panel!.active ?? false,
                        title: panel?.plotTitle ?? '[Title Missing]',
                    };
                }),
        ];
    }, [panelData]);

    useEffect(() => {
        getStudyDashboardConfigPanels({ variables: { studyId: studyRef } }).catch((err) => window.console.error(err));
    }, [orgKey, studyRef]);

    const [currentState, setCurrentState] = useState<ConfigPanelsStateProps>({
        display: [],
        available: [],
    });

    const [panelsToSave, setPanelsToSave] = useState<IPanel[]>([]);

    const initialState = useRef<ConfigPanelsStateProps>({
        display: [],
        available: [],
    });

    // Order the selected panels based on the order of the order of the config
    const reorderPanels = (array: IPanel[], configOrder: string[]) => {
        array.sort((a, b) => {
            if (configOrder.indexOf(b.reference) < configOrder.indexOf(a.reference)) {
                return 1;
            }
            return -1;
        });
    };

    useEffect(() => {
        const availablePanels = allPanels.filter(
            (panel) => !selectedPanelRefs.some((selectedPanelRef) => selectedPanelRef === panel.reference),
        );
        const selectedPanels = allPanels.filter((panel) =>
            selectedPanelRefs.some((selectedPanelRef) => selectedPanelRef === panel.reference),
        );

        reorderPanels(selectedPanels, selectedPanelRefs);

        setCurrentState({
            display: selectedPanels,
            available: availablePanels,
        });

        setPanelsToSave(selectedPanels);
        // Shallow copy, so when resetting currentState to initialState, it compares different refs
        initialState.current = {
            display: [...selectedPanels],
            available: [...availablePanels],
        };
        isEditMode.setFalse();
    }, [panelData, selectedPanelRefs]);

    const onEdit = () => {
        isEditMode.setTrue();
    };

    const onCancel = () => {
        setCurrentState({
            display: initialState.current.display,
            available: initialState.current.available,
        });
        setPanelsToSave(initialState.current.display);
        isEditMode.setFalse();
    };

    const onSave = () => {
        const panelRefs = panelsToSave.map((panel) => panel.reference);
        const hasChanged =
            JSON.stringify(panelRefs) !== JSON.stringify(initialState.current.display.map((panel) => panel.reference));
        if (!hasChanged) {
            isEditMode.setFalse();
            return;
        }
        saveConfig({
            variables: {
                studyId: studyRef,
                panelRefs,
            },
        })
            .then((response) => {
                if (!response?.data?.addStudyDashboardConfigPanels) {
                    ShowError(t('errorMessage'));
                    return;
                }

                isEditMode.setFalse();
                setComponentHasChanges(COMPONENT_NAME, false);
                ShowSuccess(t('actionsSuccessfulToast'));
            })
            .catch(() => {
                ShowError(t('errorMessage'));
            });
    };

    const hasChanges =
        JSON.stringify(panelsToSave.map((w) => w.reference)) !==
        JSON.stringify(initialState.current.display.map((w) => w.reference));

    const onEditCardCancel = async () => {
        if (hasChanges) {
            const ok = await confirmDiscardChanges('confirmation-dialog');
            if (!ok) {
                return;
            }
            onCancel();
        } else {
            onCancel();
        }
    };

    useEffect(() => {
        setComponentHasChanges(COMPONENT_NAME, hasChanges);
        return () => {
            setComponentHasChanges(COMPONENT_NAME, false);
        };
    }, [hasChanges]);

    useEffect(() => {
        if (!isEditMode.value) {
            setComponentHasChanges(COMPONENT_NAME, false);
        }
        return () => {
            setComponentHasChanges(COMPONENT_NAME, false);
        };
    }, [isEditMode.value]);

    const isLoading = isMutatingStudyPanelsConfigData;
    const isError = panelsMutationError;

    return (
        <EditCard
            title={t('cm:panels')}
            onEdit={onEdit}
            onCancel={onEditCardCancel}
            onSave={onSave}
            isEditMode={isEditMode.value}
            disabled={isLoading || !canEdit}
            data-testid="panels-config"
            saveDisabled={!hasChanges}
        >
            {isLoading && !isError && <Loading />}
            {isError && <ErrorMessage />}
            {!isLoading && !isError && (
                <div style={{ height: '230px' }}>
                    <DragDropListsCore
                        list1Header={t('study:panelsDisplay')}
                        list2Header={t('study:availablePanels')}
                        list1Items={currentState.display}
                        list2Items={currentState.available}
                        isTwoLists
                        idField="reference"
                        onChangeItemsLeft={(display) => {
                            const saveDisplay = display.map((item) => item as unknown as IPanel);
                            setPanelsToSave(saveDisplay);
                        }}
                        disabled={!isEditMode.value}
                        className="k-mb-4"
                    />
                </div>
            )}
        </EditCard>
    );
};

export default PanelsConfig;
